import { Download } from '@mui/icons-material';
import { ControlAreaBase } from '@src/components/common/ControlAreaBase';
import type { RouterPath } from '@src/constants/router_path';
import { useControlAreaKeyboard } from '@src/hooks/useControlAreaKeyboard';
import { useControlAreaNavigation } from '@src/hooks/useControlAreaNavigation';
import { useControlAreaScroll } from '@src/hooks/useControlAreaScroll';
import { useControlAreaStyles } from '@src/hooks/useControlAreaStyles';
import { usePdfDownload } from '@src/hooks/usePdfDownload';
import { useTranslation } from '@src/modules/react_hooks/useTranslation';
import type { RootState } from '@src/store/index';
import { selectFocusMode } from '@src/store/slices/focus_mode';
import type { TranslationKeys } from '@src/types/i18n';
import { memo, type ReactElement, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

/**
 * 入力操作エリアコンポーネントのProps型定義
 */
type InputControlAreaProps = {
  isScrolling?: boolean;
  isInputActive?: boolean;
  isInline?: boolean; // インライン表示用のプロパティ
  hideScrollToTop?: boolean; // 上部スクロールボタンを非表示にするプロパティ
  backPath?: RouterPath; // 前のページへのパス
  nextPath?: RouterPath; // 次のページへのパス
  contentType?: 'resume' | 'rirekisho'; // 経歴書か履歴書かを指定
};

/**
 * 入力操作エリアコンポーネント
 *
 * 入力画面での前へ・次へナビゲーションと上部スクロール機能を提供します。
 * PDF操作エリアと同様の挙動をする統合された操作パネルです。
 *
 * @param {InputControlAreaProps} props - コンポーネントのプロパティ
 * @param {boolean} [props.isScrolling] - スクロール中かどうか
 * @returns {ReactElement | null} 入力操作エリアコンポーネント
 *
 * @example
 * ```tsx
 * <InputControlArea isScrolling={false} />
 * ```
 */
const InputControlArea = memo(
  ({
    isScrolling = false,
    isInputActive = false,
    isInline = false, // インライン表示用のプロパティ
    hideScrollToTop = false, // 上部スクロールボタンを非表示にするプロパティ
    contentType = 'resume', // デフォルトは経歴書
  }: InputControlAreaProps): ReactElement | null => {
    const { t } = useTranslation();

    // フォーカスモード状態を取得
    const isFocusMode = useSelector(selectFocusMode) || false;

    // 進捗状態を取得
    const inputState: number = useSelector(
      (state: RootState) => state.unifiedInputStatus.resume.status
    );

    // PDF生成関連 - 共通フックを使用
    const { isPdfGenerating, handlePdfDownload } = usePdfDownload();
    const [previousProgress, setPreviousProgress] = useState(inputState);
    const [isProgressAnimating, setIsProgressAnimating] = useState(false);

    // 共通フックを使用
    const currentPageInfo = useControlAreaNavigation(contentType);
    const scrollHandlers = useControlAreaScroll(currentPageInfo);
    const { containerStyles, buttonStyles } = useControlAreaStyles({
      isFocusMode,
      isScrolling,
      isInputActive,
      isInline,
      contentType,
    });

    // キーボードショートカットを設定
    useControlAreaKeyboard(currentPageInfo, scrollHandlers);

    // 進捗増加時のアニメーション効果
    useEffect(() => {
      if (inputState > previousProgress) {
        setIsProgressAnimating(true);
        const timer = setTimeout(() => {
          setIsProgressAnimating(false);
        }, 1200);
        return () => clearTimeout(timer);
      }
      setPreviousProgress(inputState);
    }, [inputState, previousProgress]);

    // メインコンテンツページ以外では表示しない
    const pageLabel = useMemo(() => {
      if (!currentPageInfo) {
        return '';
      }
      return t(
        (currentPageInfo.currentPageTitle || currentPageInfo.currentName) as TranslationKeys
      );
    }, [currentPageInfo, t]);

    const progressOptions = useMemo(
      () => ({
        containerSx: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: isFocusMode ? 0.25 : 0.125,
          px: isFocusMode ? 0.1 : 0.6,
          py: isFocusMode ? 0.1 : 0.4,
        },
        textSx: {
          fontSize: { xs: '0.45rem', sm: '0.5rem' },
        },
        animate: true,
        animationActive: isProgressAnimating,
      }),
      [isFocusMode, isProgressAnimating]
    );

    const downloadButton = useMemo(() => {
      if (!isFocusMode) {
        return undefined;
      }

      return {
        icon: <Download fontSize="small" />,
        onClick: handlePdfDownload,
        ariaLabel: isPdfGenerating ? 'PDF作成中...' : 'PDFをダウンロード',
        disabled: isPdfGenerating,
        sx: {
          ...buttonStyles.download,
          ...(isPdfGenerating && {
            animation: 'pulse 1.5s ease-in-out infinite',
            '@keyframes pulse': {
              '0%': { transform: 'scale(1)', opacity: 1 },
              '50%': { transform: 'scale(1.05)', opacity: 0.8 },
              '100%': { transform: 'scale(1)', opacity: 1 },
            },
          }),
        },
      };
    }, [buttonStyles.download, handlePdfDownload, isFocusMode, isPdfGenerating]);

    return (
      <ControlAreaBase
        pageInfo={currentPageInfo}
        containerStyles={containerStyles}
        buttonStyles={buttonStyles}
        pageLabel={pageLabel}
        pageTitle={pageLabel}
        progress={inputState}
        progressOptions={progressOptions}
        isFocusMode={isFocusMode}
        isScrolling={isScrolling}
        isInputActive={isInputActive}
        onPrev={scrollHandlers.handlePrevPage}
        onNext={scrollHandlers.handleNextPage}
        onScrollTop={hideScrollToTop ? undefined : scrollHandlers.handleScrollToTop}
        showScrollTop={!hideScrollToTop}
        downloadButton={downloadButton}
      />
    );
  }
);

InputControlArea.displayName = 'InputControlArea';

export default InputControlArea;
